/**
 * Selection Type Operations - Funktioner för att rendera selection type features
 */

import * as salesDistrictsOps from '../districts/sales-districts-operations';
import { featureCollectionToTurfPolygons } from '../utils/createTurfPolygons';
import * as labelOps from './label-operations';
import * as mapOps from './map-operations';
import * as mapState from './map-state';
import * as districtCache from '../districts/district-cache';

const ALL_SELECTION_TYPES = ['zip1', 'zip2', 'zip3', 'zip5', 'county', 'municipality', 'aregion', 'postalcity'];

const defaultStyle = {
    strokeWeight: 1.5, 
    fillColor: '#ff0000', 
    fillOpacity: 0, 
    strokeColor: '#cc0000',
    visible: true,
    clickable: false,
    zIndex: 4
};

/**
 * Apply styles to selection type features.
 * Uses per-layer feature ID lists from mapState for O(n_layer) lookups via
 * map.data.getFeatureById, avoiding a full map.data.forEach iteration.
 */
const applySelectionTypeStyles = (map, style, selectionTypes = ALL_SELECTION_TYPES) => {
    const mergedStyle = { ...defaultStyle, ...style };

    for (const layerType of selectionTypes) {
        const featureIds = mapState.getLayerFeatureIds(layerType);
        if (!featureIds) continue;
        for (const id of featureIds) {
            const feature = map.data.getFeatureById(id);
            if (feature) {
                map.data.overrideStyle(feature, mergedStyle);
            }
        }
    }
}

/**
 * Remove features from map.data for all non-active layers.
 * Keeps selected features so they remain visible with district color.
 */
function removeNonActiveLayersFromMap(map) {
    const selectedAreaIds = new Set();
    const selectedAreas = districtCache.getCurrentDistrictSelectedAreas();
    if (selectedAreas) {
        for (const area of selectedAreas) {
            if (area?.properties?.id) selectedAreaIds.add(area.properties.id);
        }
    }

    for (const layerType of ALL_SELECTION_TYPES) {
        if (!mapState.isLayerOnMap(layerType)) continue;

        const featureIds = mapState.getLayerFeatureIds(layerType);
        if (!featureIds) continue;

        for (const id of featureIds) {
            if (selectedAreaIds.has(id)) continue;
            const feature = map.data.getFeatureById(id);
            if (feature) {
                map.data.remove(feature);
            }
        }

        mapState.removeLayerFromMap(layerType);
    }
}

/**
 * Add a previously fetched layer's features back to map.data.
 * Skips features that are already on the map (e.g. selected features kept from a previous clear).
 */
function addLayerToMap(type) {
    const featureCollection = mapState.getFeatureCollection(type);
    if (!featureCollection) return;

    const map = mapState.getMap();
    const featuresToAdd = featureCollection.features.filter(f => !map.data.getFeatureById(f.id));

    if (featuresToAdd.length > 0) {
        map.data.addGeoJson({ type: 'FeatureCollection', features: featuresToAdd });
        for (const f of featuresToAdd) {
            const feature = map.data.getFeatureById(f.id);
            if (feature) {
                map.data.overrideStyle(feature, defaultStyle);
            }
        }
    }

    mapState.setLayerOnMap(type);
}

/**
 * Förladda en layer (GeoJSON) om den inte redan är laddad
 * @param {string} type - Layer type (t.ex. 'zip1', 'zip3', etc.)
 * @param {Object} mapOps - Map operations (used as mapState accessor)
 * @param {Function} fetchGeoJSON - Function to fetch GeoJSON
 */
export async function preloadLayer(type, mapOps, fetchGeoJSON) {
    if (!mapState.hasLayerType(type)) {
        const featureCollection = await fetchGeoJSON(type);

        featureCollection.features.forEach(feature => {
            feature.id = feature.properties.id;
        });

        mapState.setFeatureCollection(type, featureCollection);
        const map = mapState.getMap();
        map.data.addGeoJson(featureCollection);
        // Per-feature styling — global setStyle() skriver över Terra Draws style-funktion
        // och ger standard Google-markörer vid ritade polygoner.
        featureCollection.features.forEach((f) => {
            const feature = map.data.getFeatureById(f.id);
            if (feature) {
                map.data.overrideStyle(feature, defaultStyle);
            }
        });

        mapState.updateTurfPolygons({
            [type]: featureCollectionToTurfPolygons(featureCollection)
        });
        mapState.setLayerOnMap(type);
    } else if (!mapState.isLayerOnMap(type)) {
        addLayerToMap(type);
    }
}


/**
 * Render features för en given selection type
 * @param {Object} params - Render parameters
 * @param {string} params.type - Selection type
 * @param {Function} params.fetchGeoJSON - Function to fetch GeoJSON
 * @param {boolean} params.isSalesDistricts - Whether sales districts are visible
 */
export async function renderSelectionTypeFeatures({
    type,
    fetchGeoJSON,
    isSalesDistricts
}) {
    // Hantera clear selection
    if (type === 'clear') {
        labelOps.removeSelectionLabels();
        
        removeNonActiveLayersFromMap(mapOps.getMap());
        
        labelOps.handleLabels({
            map: mapOps.getMap(),
            maps: mapOps.getMaps(),
            turfPolygons: mapOps.getAllTurfPolygons(),
            selectionType: 'clear',
            isSalesDistricts,
            salesDistricts: salesDistrictsOps.getSalesDistricts()
        });
        
        return;
    }

    // Förladda layer om den inte redan är laddad
    await preloadLayer(
        type,
        mapOps,
        fetchGeoJSON
    );

    // Applicera styles för selection type features
    applySelectionTypeStyles(mapOps.getMap(), {
        visible: true
    }, [type]);

    // Uppdatera labels för den nya selection type
    labelOps.removeAllLabels();
    
    labelOps.handleLabels({
        map: mapOps.getMap(),
        maps: mapOps.getMaps(),
        turfPolygons: mapOps.getAllTurfPolygons(),
        selectionType: districtCache.getCurrentDistrictSelectionType(),
        isSalesDistricts,
        salesDistricts: salesDistrictsOps.getSalesDistricts()
    });
}
